/**
 * Attendance overview — company-wide attendance grid.
 *
 * Composition:
 *   - PageHeader + "Mark attendance" action
 *   - KPI strip (Today's attendance %, Week %, Most absent, OT this month)
 *   - Branch / department / window filters
 *   - AttendanceMatrix (rows = employees × columns = days)
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarDays, Filter, Plus } from "lucide-react";

import {
  Button,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { AttendanceMatrix } from "../../../components/workforce/AttendanceMatrix";
import {
  AttendanceEntryForm,
  type AttendanceEntryFormValues,
} from "../../../components/forms/AttendanceEntryForm";
import { RequirePermission } from "../../../lib/permissions";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  DEPARTMENTS,
  WORKFORCE_BRANCHES,
  mockAttendance,
  mockEmployees,
  type MockAttendance,
  type MockAttendanceStatus,
  type MockDepartment,
  type MockEmployee,
} from "../../../lib/workforceMock";

export const Route = createFileRoute("/_app/workforce/attendance")({
  component: AttendancePage,
});

const DAY_MS = 24 * 60 * 60 * 1000;

function AttendancePage() {
  const [employees] = usePersistentMockState<MockEmployee[]>("employees", mockEmployees);
  const [rows, setRows] = usePersistentMockState<MockAttendance[]>("attendance", mockAttendance);
  const [createOpen, setCreateOpen] = useState(false);

  const [branchFilter, setBranchFilter] = useState<string | "all">("all");
  const [deptFilter, setDeptFilter] = useState<MockDepartment | "all">("all");
  const [windowDays, setWindowDays] = useState<7 | 14 | 30>(14);

  // Active employees scoped by current filters
  const visibleEmployees = useMemo(() => {
    return employees
      .filter((e) => e.isActive)
      .filter((e) => (branchFilter === "all" ? true : e.branchId === branchFilter))
      .filter((e) => (deptFilter === "all" ? true : e.department === deptFilter));
  }, [employees, branchFilter, deptFilter]);

  const visibleEmpIds = useMemo(
    () => new Set(visibleEmployees.map((e) => e._id)),
    [visibleEmployees],
  );
  const filteredAttendance = useMemo(
    () => rows.filter((r) => visibleEmpIds.has(r.employeeId)),
    [rows, visibleEmpIds],
  );

  // KPIs
  const todayUtc = (() => {
    const d = new Date();
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  })();
  const todayRows = filteredAttendance.filter((r) => r.date === todayUtc);
  const todayPresent = todayRows.filter(
    (r) => r.status === "present" || r.status === "half_day",
  ).length;
  const todayPct =
    visibleEmployees.length === 0
      ? 0
      : Math.round((todayPresent / visibleEmployees.length) * 100);

  const weekStart = todayUtc - 6 * DAY_MS;
  const weekRows = filteredAttendance.filter(
    (r) => r.date >= weekStart && r.date <= todayUtc,
  );
  let weekScore = 0;
  let weekScheduled = 0;
  for (const r of weekRows) {
    if (r.status === "holiday") continue;
    weekScheduled += 1;
    if (r.status === "present") weekScore += 1;
    else if (r.status === "half_day") weekScore += 0.5;
  }
  const weekPct =
    weekScheduled === 0 ? 0 : Math.round((weekScore / weekScheduled) * 100);

  // Most absent (top 3)
  const absentByEmp = new Map<string, number>();
  for (const r of filteredAttendance) {
    if (r.status === "absent") {
      absentByEmp.set(r.employeeId, (absentByEmp.get(r.employeeId) ?? 0) + 1);
    }
  }
  const mostAbsent = Array.from(absentByEmp.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 1)
    .map(([id, count]) => {
      const emp = employees.find((e) => e._id === id);
      return emp ? `${emp.fullName.split(" ")[0]} (${count})` : `${count}`;
    })
    .join(", ");

  // OT this month
  const month = new Date().getUTCMonth();
  const year = new Date().getUTCFullYear();
  const otMinutes = filteredAttendance.reduce((s, r) => {
    const d = new Date(r.date);
    if (d.getUTCMonth() === month && d.getUTCFullYear() === year) {
      return s + r.overtimeMinutes;
    }
    return s;
  }, 0);

  function handleNewEntry(values: AttendanceEntryFormValues) {
    const date = new Date(values.date);
    const dateMs = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
    );
    const checkIn =
      values.status === "present" || values.status === "half_day"
        ? dateMs +
          Number(values.checkIn.split(":")[0]) * 60 * 60 * 1000 +
          Number(values.checkIn.split(":")[1]) * 60 * 1000
        : null;
    const checkOut =
      values.status === "present" || values.status === "half_day"
        ? dateMs +
          Number(values.checkOut.split(":")[0]) * 60 * 60 * 1000 +
          Number(values.checkOut.split(":")[1]) * 60 * 1000
        : null;
    const workedMinutes =
      checkIn && checkOut ? Math.max(0, Math.floor((checkOut - checkIn) / 60000)) : 0;

    setRows((prev) => {
      // Upsert by (employeeId, date)
      const idx = prev.findIndex(
        (r) => r.employeeId === values.employeeId && r.date === dateMs,
      );
      const baseRow: MockAttendance = {
        _id: idx >= 0 ? prev[idx]!._id : `att_local_${Date.now()}`,
        employeeId: values.employeeId,
        date: dateMs,
        status: values.status,
        checkInAt: checkIn,
        checkOutAt: checkOut,
        workedMinutes,
        overtimeMinutes: values.overtimeMinutes,
        notes: values.notes || null,
      };
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = baseRow;
        return next;
      }
      return [baseRow, ...prev];
    });
    setCreateOpen(false);
    toast.success("Attendance recorded");
  }

  function handleToggle(
    employeeId: string,
    date: number,
    newStatus: MockAttendanceStatus | null,
    extras?: {
      checkInAt?: number | null;
      checkOutAt?: number | null;
      overtimeMinutes?: number;
      notes?: string | null;
    },
  ) {
    setRows((prev) => {
      const idx = prev.findIndex(
        (r) => r.employeeId === employeeId && r.date === date,
      );
      if (newStatus === null) {
        // Remove the record
        if (idx >= 0) {
          const next = [...prev];
          next.splice(idx, 1);
          return next;
        }
        return prev;
      }
      // Prefer extras from the popup editor; fall back to status-based defaults
      // for statuses that don't have time semantics.
      const usesTimes = newStatus === "present" || newStatus === "half_day";

      const checkIn = usesTimes
        ? (extras?.checkInAt ?? date + 9 * 60 * 60 * 1000) // default 09:00
        : null;
      const checkOut = usesTimes
        ? (extras?.checkOutAt ??
            (newStatus === "present"
              ? date + 18 * 60 * 60 * 1000
              : date + 13 * 60 * 60 * 1000))
        : null;
      const workedMinutes =
        checkIn && checkOut
          ? Math.max(0, Math.floor((checkOut - checkIn) / 60000))
          : 0;
      const overtimeMinutes = usesTimes ? (extras?.overtimeMinutes ?? 0) : 0;
      const notes = extras?.notes ?? null;

      const record: MockAttendance = {
        _id: idx >= 0 ? prev[idx]!._id : `att_toggle_${Date.now()}_${employeeId}`,
        employeeId,
        date,
        status: newStatus,
        checkInAt: checkIn,
        checkOutAt: checkOut,
        workedMinutes,
        overtimeMinutes,
        notes,
      };
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = record;
        return next;
      }
      return [...prev, record];
    });
  }

  function handleExportCsv(extension: "csv" | "xls") {
    type AttRow = {
      date: string;
      employeeCode: string;
      employeeName: string;
      department: string;
      branch: string;
      status: string;
      checkIn: string;
      checkOut: string;
      workedMinutes: number;
      overtimeMinutes: number;
      notes: string;
    };
    const empById = new Map(employees.map((e) => [e._id, e]));
    const rows: AttRow[] = filteredAttendance.map((r) => {
      const emp = empById.get(r.employeeId);
      return {
        date: formatDateForCsv(r.date),
        employeeCode: emp?.code ?? "",
        employeeName: emp?.fullName ?? "",
        department: emp?.department ?? "",
        branch: emp?.branchLabel ?? "",
        status: r.status,
        checkIn: r.checkInAt
          ? new Date(r.checkInAt).toISOString().slice(0, 16).replace("T", " ")
          : "",
        checkOut: r.checkOutAt
          ? new Date(r.checkOutAt).toISOString().slice(0, 16).replace("T", " ")
          : "",
        workedMinutes: r.workedMinutes,
        overtimeMinutes: r.overtimeMinutes,
        notes: r.notes ?? "",
      };
    });
    const columns: ExportColumn<AttRow>[] = [
      { key: "date", label: "Date" },
      { key: "employeeCode", label: "Employee code" },
      { key: "employeeName", label: "Employee" },
      { key: "department", label: "Department" },
      { key: "branch", label: "Branch" },
      { key: "status", label: "Status" },
      { key: "checkIn", label: "Check in" },
      { key: "checkOut", label: "Check out" },
      { key: "workedMinutes", label: "Worked min" },
      { key: "overtimeMinutes", label: "OT min" },
      { key: "notes", label: "Notes" },
    ];
    downloadCsv(
      exportFilename("attendance", extension),
      toCsv(rows, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Attendance"
        description="Daily presence across the workforce — present, absent, leave, holiday."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <RequirePermission permission="attendance.write">
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" /> Mark attendance
              </Button>
            </RequirePermission>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Attendance KPIs"
      >
        <KpiCard caption="Today" value={`${todayPct}%`} hint="Present + half-day" />
        <KpiCard caption="Last 7 days" value={`${weekPct}%`} hint="Scheduled basis" />
        <KpiCard
          caption="Most absent"
          value={mostAbsent || "—"}
          hint="Window total"
        />
        <KpiCard
          caption="Overtime this month"
          value={`${(otMinutes / 60).toFixed(1)}h`}
          hint={`${otMinutes} minutes`}
        />
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-(--text-tertiary)" aria-hidden />
        <FilterChip
          active={branchFilter === "all"}
          onClick={() => setBranchFilter("all")}
        >
          All branches
        </FilterChip>
        {WORKFORCE_BRANCHES.map((b) => (
          <FilterChip
            key={b.id}
            active={branchFilter === b.id}
            onClick={() => setBranchFilter(b.id)}
          >
            {b.displayName}
          </FilterChip>
        ))}
        <span className="mx-1 text-(--text-tertiary) text-[11px]">·</span>
        <FilterChip
          active={deptFilter === "all"}
          onClick={() => setDeptFilter("all")}
        >
          All depts
        </FilterChip>
        {DEPARTMENTS.map((d) => (
          <FilterChip
            key={d}
            active={deptFilter === d}
            onClick={() => setDeptFilter(d)}
          >
            {d}
          </FilterChip>
        ))}
        <span className="mx-1 text-(--text-tertiary) text-[11px]">·</span>
        {([7, 14, 30] as const).map((n) => (
          <FilterChip
            key={n}
            active={windowDays === n}
            onClick={() => setWindowDays(n)}
          >
            {n} days
          </FilterChip>
        ))}
      </div>

      <AttendanceMatrix
        employees={visibleEmployees}
        attendance={filteredAttendance}
        days={windowDays}
        onToggle={handleToggle}
      />

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Mark attendance</SheetTitle>
            <SheetDescription>
              Records or updates attendance for a single employee on a single day.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <AttendanceEntryForm
              employees={employees.filter((e) => e.isActive)}
              onSubmit={handleNewEntry}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {visibleEmployees.length === 0 ? (
        <div className="text-[12px] text-(--text-tertiary) inline-flex items-center gap-2 self-center">
          <CalendarDays className="h-3.5 w-3.5" />
          No employees match the current filters.
        </div>
      ) : null}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-7 rounded-full border px-2.5 text-[11px] transition-colors",
        active
          ? "bg-(--accent) text-(--accent-fg) border-(--accent)"
          : "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle) hover:bg-(--bg-hover)",
      )}
    >
      {children}
    </button>
  );
}
